import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { GAP_GUIDANCE, INTAKE_GAPS, type IntakeGap } from "@titleverify/shared";
import { apiErrorText, apiFetch, type Case, type IntakeSubmission } from "../api.js";
import { useAuth } from "../auth.js";
import { ADVISORY_PROMPT } from "../intakeCopy.js";

/**
 * THE WAY OUT OF NEEDS_INFO.
 * ===========================================================================
 *
 * Until this existed a client whose case came back NEEDS_INFO could not ever
 * finish it. The intake screen admitted as much — "There is no screen for
 * editing a request yet, so send it to us directly and quote the reference" —
 * which made the platform's own dead end into the client's errand.
 *
 * THE FORM ASKS FOR EXACTLY WHAT THE SERVER SAID IS MISSING, AND NOTHING ELSE.
 * That is not minimalism, it is forced by two facts about the API:
 *
 *  1. NO ENDPOINT RETURNS A PARCEL. `present()` in workflow/routes.ts sends the
 *     case — status, registry district, land system, gaps — and no parcel field
 *     at all. So this form CANNOT be prefilled with what the client already told
 *     us. A full parcel form rendered with empty boxes would say "we hold
 *     nothing for these fields", which is a fabricated fact about our own
 *     records (CLAUDE.md non-negotiable 1).
 *  2. EMPTY STRING IS DESTRUCTIVE ON THIS ENDPOINT, and it is the opposite of
 *     what a form author expects. The merge is `patch.X ?? existing.X`: omitted
 *     or null KEEPS the stored value, but "" is not nullish, so it WINS and
 *     overwrites. A form that posted every box on every submit would silently
 *     wipe the district and survey plan the client gave at intake and come back
 *     with MORE gaps than it started with. So `patch()` below sends only keys
 *     the client actually typed into, and there is deliberately no way to clear
 *     a field from here.
 *
 * Together those make "add what is missing" the only honest framing: this is an
 * add form, not an edit form, and the copy says so.
 *
 * THE SUFFICIENCY RULE IS NOT MIRRORED HERE. Nothing in this file decides
 * whether what the client typed is enough — no `required` attributes standing in
 * for a rule, no "that looks complete" hint. `assessParcel` in packages/shared
 * is the one place that judgement lives, in terms an attorney can read and
 * correct (it is TODO-BLOCKED-INTAKE-SUFFICIENCY and can change under this
 * file). A browser verdict that disagreed with the server's is exactly the drift
 * keeping the rule in one file prevents. The submit button's only condition is
 * whether the client has typed anything at all, which is a fact about the form
 * and not a judgement about the land.
 */
export function SupplementCase({ caseRow }: { caseRow: Case }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [outcome, setOutcome] = useState<IntakeSubmission | null>(null);

  /*
   * What was outstanding at the moment the client pressed send, so the result
   * can be described as progress, as resolved, or as unchanged. Captured on
   * submit rather than read at render time, because the case query is
   * invalidated on success and `caseRow` refreshes underneath this component —
   * comparing against the refreshed row would compare the answer with itself.
   */
  const askedFor = useRef<IntakeGap[]>([]);

  const supplement = useMutation({
    mutationFn: async () =>
      apiFetch<IntakeSubmission>(`/cases/${caseRow.id}/parcel`, {
        token: await getToken(),
        method: "PATCH",
        // The body IS the parcel. No { parcel: … } envelope — that wrapper
        // exists only on POST /cases, and sending it here would parse to an
        // empty patch and quietly change nothing.
        body: patch(form),
      }),
    onSuccess: (result) => {
      setOutcome(result);
      setForm(EMPTY);
      void queryClient.invalidateQueries({ queryKey: ["cases", "mine"] });
      void queryClient.invalidateQueries({ queryKey: ["case", caseRow.id] });
    },
  });

  const gaps = answerableGaps(caseRow.needsInfoGaps);
  const theirs = gaps.filter((gap) => gap !== "TITLE_SYSTEM_NOT_YET_SUPPORTED");
  const ours = gaps.includes("TITLE_SYSTEM_NOT_YET_SUPPORTED");
  const open = caseRow.status === "NEEDS_INFO";

  /*
   * Nothing to draw, and nothing to apologise for. Note the outcome check: a
   * supplement that resolved the case flips it out of NEEDS_INFO, and if this
   * returned null on that basis the panel telling the client it worked would be
   * unmounted by its own success.
   */
  if (!open && outcome === null) return null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    askedFor.current = theirs;
    supplement.mutate();
  }

  const typedSomething = FIELDS.some((field) => form[field].trim() !== "");

  return (
    <div className="stack">
      {outcome ? <Result result={outcome} askedFor={askedFor.current} /> : null}

      {open ? (
        <form onSubmit={onSubmit}>
          {/*
            THE HEADING A STRANDED CLIENT ACTUALLY LANDS ON.
            "Add what is missing" names the deficit and puts it on them. What is
            true — and more useful — is that the request is safe and short by one
            thing. The mechanical facts underneath (nothing else is re-typed,
            blanks do not clear anything) were already right and are unchanged;
            only what surrounds them moved.
          */}
          <h3>Add the last piece</h3>
          <p>
            Your request is open and nothing about it is at risk. Only the details below are
            still outstanding.
          </p>
          <p>
            <small>
              Everything else you gave us is held against this request and is not shown here to
              be re-typed. Anything you leave blank stays exactly as it is; nothing on this form
              clears a value, so there is no way to make things worse by trying.
            </small>
          </p>

          {theirs.map((gap) => (
            <GapFields
              key={gap}
              gap={gap}
              caseId={caseRow.id}
              form={form}
              onChange={(field, value) => setForm((f) => ({ ...f, [field]: value }))}
            />
          ))}

          {supplement.error ? (
            /*
             * A GENUINE FAILURE — nothing was saved. Distinct from a case that
             * comes back still needing a detail, which is a 200 and is rendered
             * as a result rather than an alarm.
             *
             * apiErrorText surfaces the server's own message where it wrote
             * one, and the useful one here is real: "This case is ROUTED and is
             * no longer accepting changes to the parcel details." That happens
             * when the case moved on in another tab or by an operator's hand,
             * and it is a far better answer than anything invented.
             */
            <div role="alert">
              <p>{apiErrorText(supplement.error, "Could not add these details to your request.")}</p>
              {/*
                What is still true after the failure. `supplement` failing does
                not touch `form` — it is cleared only in onSuccess — so every box
                still holds what was typed. Worth saying: this form appears at
                the end of a case card, and a person who thinks their retyping
                vanished will not scroll back to check.
              */}
              <p>
                <small>
                  <strong>What you typed is still in the boxes below.</strong> Your request is
                  unchanged and nothing was removed from it — you can try again without
                  entering any of it a second time.
                </small>
              </p>
            </div>
          ) : null}

          <div className="actions">
            <button type="submit" disabled={!typedSomething || supplement.isPending}>
              {supplement.isPending ? "Sending…" : "Add these to my request"}
            </button>
          </div>
        </form>
      ) : null}

      {/*
        OUR LIMITATION, KEPT WELL AWAY FROM THE LIST OF THINGS THEY MUST GO AND
        FIND. Same treatment as the intake screen deliberately: a client who
        ticked "title registration" has done nothing wrong, and putting our
        inability to search Bim Saviya parcels in a bulleted list headed "add
        what is missing" would read as their failing and send them looking for a
        document that would not help.
      */}
      {ours ? <NotYours /> : null}
    </div>
  );
}

/** The fields this form can send. Every one of them is named by some gap; there are no extras. */
const FIELDS = [
  "district",
  "surveyPlanNo",
  "lotNo",
  "currentDeedNo",
  "currentDeedDate",
  "currentDeedNotary",
] as const;

type Field = (typeof FIELDS)[number];

const EMPTY: Record<Field, string> = {
  district: "",
  surveyPlanNo: "",
  lotNo: "",
  currentDeedNo: "",
  currentDeedDate: "",
  currentDeedNotary: "",
};

/**
 * Only what the client typed. See the block comment at the top: on this endpoint
 * "" overwrites and omitted keeps, so an untouched box must not appear in the
 * body at all.
 *
 * This is why `orNull` from the intake form is NOT reused here. There, blank
 * means "the client did not tell us" and null is the right thing to store. Here
 * null means "keep what you have", so the two helpers look interchangeable, type
 * identically, and do opposite things.
 */
function patch(form: Record<Field, string>): Record<string, string> {
  const body: Record<string, string> = {};
  for (const field of FIELDS) {
    const value = form[field].trim();
    if (value !== "") body[field] = value;
  }
  return body;
}

/**
 * Narrows the `unknown` JSONB `needsInfoGaps` into gaps this form can act on.
 *
 * Validated rather than cast — it is a JSONB column and the response contract
 * guarantees the browser nothing about its shape.
 *
 * IT DROPS CODES THIS BUNDLE DOES NOT KNOW, and that is the opposite of what
 * `readGaps` in CaseDetail does with the same column. The difference is
 * deliberate: that one is a reading surface, where an unrecognised gap is still
 * worth showing as a bare code, and dropping it would hide a real requirement.
 * This one has to render a form CONTROL per gap, and there is no control to
 * render for a code it has never heard of. Showing the code with no box beside
 * it would ask the client for something with no way to answer.
 *
 * The two parcel-identification gaps are mutually exclusive in `assessParcel` —
 * it pushes one or the other, never both — so ordinarily this collapse does
 * nothing. It exists because if both ever did arrive, the form would render two
 * sets of the same deed inputs with duplicate ids. Preferring the narrower gap
 * is a guard against duplicate DOM, not a re-implementation of the rule: which
 * gaps exist is still entirely the server's answer.
 */
function answerableGaps(value: unknown): IntakeGap[] {
  if (!Array.isArray(value)) return [];
  const known = value.filter((entry): entry is IntakeGap =>
    (INTAKE_GAPS as readonly unknown[]).includes(entry),
  );
  const collapsed = known.includes("DEED_REFERENCE_INCOMPLETE")
    ? known.filter((gap) => gap !== "PARCEL_NOT_IDENTIFIABLE")
    : known;
  // Render in the shared package's own order rather than the column's, so the
  // form reads the same way every time regardless of how the gaps were stored.
  return INTAKE_GAPS.filter((gap) => collapsed.includes(gap));
}

/**
 * The inputs for one gap, under that gap's own wording.
 *
 * THE HINT IS GAP_GUIDANCE, RENDERED AND NOT PARAPHRASED. It is the one place
 * an attorney can read what a searcher actually needs and correct it; if they
 * change what a deed reference requires, this form asks for the corrected thing
 * without anyone remembering to edit it here.
 *
 * Ids are namespaced with the case id because a client with several NEEDS_INFO
 * cases gets one of these per card on the same page, and duplicate ids would
 * point every label at the first card's boxes.
 */
function GapFields({
  gap,
  caseId,
  form,
  onChange,
}: {
  gap: IntakeGap;
  caseId: string;
  form: Record<Field, string>;
  onChange: (field: Field, value: string) => void;
}) {
  const id = (field: Field) => `${field}-${caseId}`;

  const input = (field: Field, label: string, type: "text" | "date" = "text") => (
    <div className="field">
      <label htmlFor={id(field)}>{label}</label>
      <input
        id={id(field)}
        type={type}
        value={form[field]}
        onChange={(event) => onChange(field, event.target.value)}
      />
    </div>
  );

  if (gap === "DISTRICT_MISSING") {
    return (
      <fieldset>
        <legend>Where the land is</legend>
        <p>
          <small>{GAP_GUIDANCE.DISTRICT_MISSING}</small>
        </p>
        {input("district", "District")}
      </fieldset>
    );
  }

  if (gap === "PARCEL_NOT_IDENTIFIABLE") {
    return (
      <fieldset>
        <legend>How the land is identified</legend>
        <p>
          <small>{GAP_GUIDANCE.PARCEL_NOT_IDENTIFIABLE}</small>
        </p>
        {/*
          TWO ROUTES, EITHER SUFFICIENT — said out loud, because the trap is a
          client reading nine boxes as nine things they must go and find, and
          giving up when they hold only half of them. Which routes exist is
          GAP_GUIDANCE's statement above, not this component's; the headings
          below only group the boxes to match it.
        */}
        <p>
          <small>
            <strong>Either group below is enough on its own.</strong> Fill in whichever one
            your papers give you, and leave the other empty.
          </small>
        </p>
        {input("surveyPlanNo", "Survey plan no.")}
        {input("lotNo", "Lot no. on that plan")}
        <p>
          <small>— or —</small>
        </p>
        {input("currentDeedNo", "Current deed no.")}
        {input("currentDeedDate", "Date of that deed", "date")}
        {input("currentDeedNotary", "Notary who attested that deed")}
      </fieldset>
    );
  }

  if (gap === "DEED_REFERENCE_INCOMPLETE") {
    return (
      <fieldset>
        <legend>The deed you told us about</legend>
        <p>
          <small>{GAP_GUIDANCE.DEED_REFERENCE_INCOMPLETE}</small>
        </p>
        {input("currentDeedDate", "Date of that deed", "date")}
        {input("currentDeedNotary", "Notary who attested that deed")}
        {/*
          The deed number is offered again so a client who realises they gave
          the wrong one can send the right one. Left blank it is not sent at all
          and the stored number stands — which is why the hint says "only if",
          rather than presenting an empty box beside a number we do hold.
        */}
        {input("currentDeedNo", "Deed no. — only if the one you gave us was wrong")}
        <small className="field-hint">
          We already hold a deed number for this request. Leave that box empty unless you
          need to correct it.
        </small>
      </fieldset>
    );
  }

  return null;
}

/**
 * The gap the platform owns.
 *
 * WHY THERE IS NO CONTROL HERE. The obvious "helpful" move — a picker letting
 * the client change the registration system back to deed or to unknown — is
 * wrong twice over. It would invite a client to restate a fact about their land
 * to get past our block, when which system a parcel falls under is the
 * attorney's finding from the registry and not a form answer. And it could not
 * work anyway: the merge treats null as "keep", and "" is not a valid enum
 * value, so there is no request this form could send that returns the field to
 * unknown. A control that cannot do what it appears to do is worse than none.
 */
function NotYours() {
  return (
    <div className="notice">
      <h3>This one is ours, not yours</h3>
      <p>
        <strong>You have not done anything wrong and there is nothing to correct.</strong> We
        cannot take requests for title-registered land yet: the details we ask for describe a
        deed, and a parcel registered under the Registration of Title Act is identified by its
        cadastral map and title certificate instead. Rather than accept a case we know we
        cannot search properly, we are saying so.
      </p>
      <p>{GAP_GUIDANCE.TITLE_SYSTEM_NOT_YET_SUPPORTED}</p>
      <p>
        <small>
          Please contact us rather than trying to change the answer here — this screen has no
          way to change it, and the answer to which system applies is one the attorney
          establishes at the registry rather than one we would want you to guess at.
        </small>
      </p>
    </div>
  );
}

/**
 * WHAT CAME BACK — and the part of this screen most easily got wrong.
 *
 * A 200 IS NOT A CONGRATULATION. The server re-runs the whole sufficiency rule
 * over the merged parcel rather than ticking off what was answered, so three
 * genuinely different things arrive down the same successful response and each
 * needs its own sentence:
 *
 *  1. SUFFICIENT — the status flipped to SUBMITTED.
 *  2. PROGRESS — still NEEDS_INFO, but a DIFFERENT gap. Answering one question
 *     revealed the next. Supplying a deed number when the date and notary are
 *     still absent moves PARCEL_NOT_IDENTIFIABLE to DEED_REFERENCE_INCOMPLETE:
 *     real progress, and not sufficiency.
 *  3. UNCHANGED — still NEEDS_INFO with the same gap. What they sent did not
 *     answer it. Saying "thank you, saved" over this would leave a client
 *     waiting on a case that is waiting on them.
 *
 * ALL THREE ARE `.outcome`, NEVER `role="alert"`. Every one of them saved what
 * the client typed and re-assessed it. The danger colours would tell them their
 * work was thrown away when it was kept.
 *
 * AND NONE OF THEM SAYS ANYTHING ABOUT THE LAND. The response carries no
 * registry district and no resolved land system — routing has not run — so
 * "SUBMITTED" means the parcel can now be LOOKED FOR at a registry. It does not
 * mean it was found, that the jurisdiction is settled, or that the registration
 * system is established, and the copy for case 1 says so rather than letting a
 * result panel imply it.
 */
function Result({ result, askedFor }: { result: IntakeSubmission; askedFor: IntakeGap[] }) {
  const theirs = result.needsInfo.filter(
    (gap) => gap.code !== "TITLE_SYSTEM_NOT_YET_SUPPORTED",
  );
  const before = [...askedFor].sort().join("|");
  const after = [...theirs.map((gap) => gap.code)].sort().join("|");
  const unchanged = theirs.length > 0 && before === after;

  return (
    <div className="outcome">
      {result.needsInfo.length === 0 ? (
        <>
          <h3>That was enough — nothing further is needed from you</h3>
          <p>
            Your request is now <span className="state">{result.status}</span>, which means
            the parcel is described well enough for someone to go and look for it at a
            registry.
          </p>
          <p>
            <strong>It does not mean the land has been found or checked.</strong> Which Land
            Registry has jurisdiction, and which registration system the parcel falls under,
            have not been worked out yet — that happens next, and this page will not guess at
            either.
          </p>
        </>
      ) : unchanged ? (
        <>
          {/*
            THE HARSHEST PANEL IN THE PRODUCT BEFORE THIS CHANGE, and the one a
            client is most likely to see twice. "Saved, but…" leads with a
            concession; "It did not answer what we are still missing" reads as a
            mark out of ten. Both facts are kept exactly — what they sent WAS
            stored, and the gap IS unchanged — but the sentence no longer grades
            the attempt.
          */}
          <h3>Saved — and we are still short of the same one thing</h3>
          <p>
            What you sent was added to your request and nothing was lost. It did not turn out
            to be the detail the registry needs to locate the parcel, so the request stays at{" "}
            <span className="state">{result.status}</span> and this is still outstanding:
          </p>
          <ul>
            {theirs.map((gap) => (
              <li key={gap.code}>{guidanceFor(gap)}</li>
            ))}
          </ul>
          <p>
            If you do not have it, tell us rather than guessing at it — a wrong reference
            sends an attorney to a registry to look for land that is not there. Coming back
            to this more than once is normal, and there is no penalty for it.
          </p>
        </>
      ) : (
        <>
          <h3>Saved — and that moved things on</h3>
          <p>
            What you sent was added to your request. It answered what we asked, and checking
            the request again turned up something else we need before the search can start.
            The request is at <span className="state">{result.status}</span>:
          </p>
          <ul>
            {theirs.map((gap) => (
              <li key={gap.code}>{guidanceFor(gap)}</li>
            ))}
          </ul>
        </>
      )}

      {/*
        The gap we own, again separated from anything the client is being asked
        to go and find. It can appear here as well as on the card — the rule is
        re-run over the whole parcel, so a title-registered parcel keeps saying
        so on every response.
      */}
      {result.needsInfo.some((gap) => gap.code === "TITLE_SYSTEM_NOT_YET_SUPPORTED") ? (
        <p>
          <strong>Separately, and not something you can fix:</strong> we still cannot take
          requests for title-registered land. See the note below.
        </p>
      ) : null}

      {result.advisories.length > 0 ? (
        <>
          <h3>Not needed, but they would help</h3>
          <p>The search can go ahead without these. They tend to make it faster.</p>
          <ul>
            {result.advisories.map((code) => (
              <li key={code}>{ADVISORY_PROMPT[code]}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * The wording for a gap belongs to GAP_GUIDANCE in the shared package — the one
 * place an attorney can read the sufficiency rule and its copy together and
 * correct both. It is never restated here.
 *
 * The server sends the same string from the same table; the fallback covers only
 * the window where a deployed API knows a gap code this bundle does not, and it
 * exists because the alternative is a blocking requirement rendering as an empty
 * bullet the client never sees.
 */
function guidanceFor(gap: { code: IntakeGap; message: string }): string {
  return GAP_GUIDANCE[gap.code] ?? gap.message;
}
